import React, { useCallback, useEffect, useMemo, useState } from "react";

import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";

import {
  useCalculateOrder,
  useCompleteRealOrder,
  useGetOrder,
  useSaveOrder,
} from "api/reactQuery/queryComponents";
import {
  BSCoreServicesDtoMarketplaceOrderDto,
  BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse,
  BSCoreWebRequestsResponsesDistributorsDistributorResponse,
  BSDALMarketplaceEnumsOrderStatus,
} from "api/reactQuery/querySchemas";
import OrderInfoModal from "components/MpOrder/CreateOrder/OrderInfoModal";
import OrderInfoSection from "components/MpOrder/CreateOrder/OrderInfoSection";
import { dateFormatForBE } from "components/Utils/dayjsUtils";
import { MarketplaceOrderStatuses } from "components/Utils/enums";

import SuccessModal from "./SuccessModal";

const CreateOrder: React.FC = () => {
  const [orderData, setOrderData] =
    useState<BSCoreServicesDtoMarketplaceOrderDto>();
  const [step, setStep] = useState(0);
  const [selectedDistributor, setSelectedDistributor] =
    useState<BSCoreWebRequestsResponsesDistributorsDistributorResponse>();
  useState<BSCoreWebRequestsResponsesDistributorsDistributorResponse>();
  const [selectedDepots, setSelectedDepots] =
    useState<
      BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[]
    >();
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<Dayjs>();
  const [isAvailabilityModalOpen, setIsAvailavilityModalOpen] = useState(false);
  const [showAllProductsAvailability, setShowAllProductsAvailability] =
    useState(false);
  const [
    showMultipackProductsAvailability,
    setShowMultipackProductsAvailability,
  ] = useState(false);
  const [
    showOnInvoiceProductsAvailability,
    setShowOnInvoiceProductsAvailability,
  ] = useState(false);
  const [showGasProductsAvailability, setShowGasProductsAvailability] =
    useState(false);

  const [isPreviewOrderModalOpen, setIsPreviewOrderModalOpen] = useState(false);
  const [preparedOrder, setPreparedOrder] =
    useState<BSCoreServicesDtoMarketplaceOrderDto>();
  const [orderComment, setOrderComment] = useState<string | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isOrderCompleted, setIsOrderCompleted] = useState(false);

  const { mutate: getOrder, isLoading: initialOrderLoading } = useGetOrder({
    onError: () => {
      toast.error("Something went wrong");
    },
    onSuccess: (data) => {
      resetCalculatedData();
      resetSavedOrderResponse();
      checkProductAvailability(data);
      setIsOrderCompleted(false);
      setOrderData(data);
    },
  });

  const {
    mutate: calculateOrder,
    data: calculatedOrderResponse,
    reset: resetCalculatedData,
  } = useCalculateOrder({
    onSuccess: (data) => {
      setOrderData(data);
    },
  });

  const {
    mutate: completeOrder,
    // isLoading: isLoadingCompleteOrder,
    reset: resetCompleteOrderResponse,
  } = useCompleteRealOrder({
    onSuccess: () => {
      setIsPreviewOrderModalOpen(false);
      // set order status and show success modal
      setIsSuccessModalOpen(true);
      setIsOrderCompleted(true);
    },
  });

  const {
    data: savedOrderResponse,
    mutate: saveOrder,
    isLoading: isSaveOrderLoading,
    reset: resetSavedOrderResponse,
  } = useSaveOrder({
    onSuccess: (data) => {
      setOrderData(data);
      setPreparedOrder(data);
      checkProductAvailability(data);

      if (data?.status !== undefined && data?.status !== null) {
        // All the quantities are OK and the order can be completed. Open the preview modal.
        if (
          MarketplaceOrderStatuses[data.status] ===
          BSDALMarketplaceEnumsOrderStatus.WaitingCompletion
        ) {
          return setIsPreviewOrderModalOpen(true);
        }

        // There are not enough available units. Show error modal.
        else if (
          MarketplaceOrderStatuses[data?.status] ===
          BSDALMarketplaceEnumsOrderStatus.InProgress
        ) {
          setIsAvailavilityModalOpen(true);
        }
      }
    },
  });

  const shouldShowProductAvailability = (
    orders: BSCoreServicesDtoMarketplaceOrderDto[],
    filterType: "all" | "invoice" | "multipack" | "gas"
  ) => {
    let foundUnavailableProduct = false;

    orders.forEach((order) =>
      order.products
        .filter((product) => {
          if (filterType === "all") {
            return !product.isInvoiceProduct;
          }

          if (filterType === "invoice") {
            return product.isInvoiceProduct;
          }

          if (filterType === "gas") {
            return product.productTypeId === 1 || product.productTypeId === 2;
          }

          return product.isMultiPack;
        })
        .forEach(({ orderedUnits, currentAvailableUnits }) => {
          if (
            currentAvailableUnits !== undefined &&
            currentAvailableUnits !== null &&
            orderedUnits > currentAvailableUnits
          ) {
            return (foundUnavailableProduct = true);
          }
        })
    );

    return foundUnavailableProduct;
  };

  const checkProductAvailability = (
    data: BSCoreServicesDtoMarketplaceOrderDto
  ) => {
    if (!data.childOrders) return null;

    setShowAllProductsAvailability(
      shouldShowProductAvailability(data.childOrders, "all")
    );

    setShowMultipackProductsAvailability(
      shouldShowProductAvailability(data.childOrders, "multipack")
    );

    setShowOnInvoiceProductsAvailability(
      shouldShowProductAvailability(data.childOrders, "invoice")
    );

    setShowGasProductsAvailability(
      shouldShowProductAvailability(data.childOrders, "gas")
    );
  };

  const resetOrder = () => {
    setIsSuccessModalOpen(false);
    setIsAvailavilityModalOpen(false);
    resetCalculatedData();
    resetCompleteOrderResponse();
    resetSavedOrderResponse();
    setSelectedDeliveryDate(undefined);
    setSelectedDistributor(null);
    setSelectedDepots(null);
    setStep(0);
  };

  // If one of the children is not with status "New", it will be applied to the parent,
  // so looping is not required in this case.
  const isNewOrder =
    MarketplaceOrderStatuses[orderData?.status] ===
    BSDALMarketplaceEnumsOrderStatus.New;

  const deliveryDate: Dayjs = useMemo(
    () =>
      selectedDeliveryDate
        ? selectedDeliveryDate
        : !isNewOrder
          ? orderData?.deliveryDate && dayjs(orderData?.deliveryDate)
          : undefined,
    [selectedDeliveryDate, isNewOrder, orderData?.deliveryDate]
  );

  // We assume that every product is a physical and takes space or has weight,
  // otherwise we should check each orderData.childOrder for quantites.
  const areQuantitiesSelected =
    !!orderData?.total?.ttlVolumeInHL || !!orderData?.total?.ttlWeight || false;

  const isTotalWeightInLimit = orderData?.isTotalWeightInLimit;

  const isTruckAreaFactorInLimit = orderData?.isTruckAreaFactorInLimit;

  const notEnoughAvailableProducts = orderData?.childOrders?.some(
    (childOrder) =>
      childOrder.products.some(
        (product) =>
          product.currentAvailableUnits !== null &&
          product.orderedQuantitiesInPallets > product.currentAvailableUnits
      )
  );

  const saveAndSendDisabled =
    isSaveOrderLoading ||
    !areQuantitiesSelected ||
    notEnoughAvailableProducts ||
    !isTotalWeightInLimit ||
    !isTruckAreaFactorInLimit;

  const handleCompleteOrder = async () => {
    if (!orderData) return;

    completeOrder({
      body: { ...preparedOrder, comment: orderComment },
    });
  };

  const prepareOrder = () => {
    if (!orderData) return;

    const childOrders = orderData?.childOrders?.map((childOrder) => {
      return {
        ...childOrder,
        products: childOrder.products.filter(
          ({ parentId }) => parentId === null
        ),
        deliveryDate: dayjs(deliveryDate).format(dateFormatForBE),
      };
    });

    saveOrder({
      body: {
        ...orderData,
        childOrders: childOrders,
        deliveryDate: dayjs(deliveryDate).format(dateFormatForBE),
      },
    });
  };

  useEffect(() => {
    if (selectedDepots?.length > 0 && step === 1) {
      getOrder({
        body: {
          deliveryDate: dayjs(deliveryDate).format(dateFormatForBE),
          depotIds: selectedDepots?.map((depot) => depot.id),
        },
      });
    }
  }, [selectedDepots, step, deliveryDate]);

  return (
    <>
      <OrderInfoSection
        initialOrderLoading={initialOrderLoading}
        resetCalculatedData={resetCalculatedData}
        selectedDepots={selectedDepots}
        setSelectedDepots={setSelectedDepots}
        selectedDistributor={selectedDistributor}
        setSelectedDistributor={setSelectedDistributor}
        deliveryDate={deliveryDate}
        setDeliveryDate={setSelectedDeliveryDate}
        saveAndSendDisabled={saveAndSendDisabled}
        saveAndSendLoading={isSaveOrderLoading}
        prepareOrder={prepareOrder}
        dateCreated={
          orderData?.dateCreated &&
          dayjs(orderData?.dateCreated)?.add(
            dayjs(orderData?.dateCreated).utcOffset(),
            "minutes"
          )
        }
        showCountdown={!isNewOrder && !isOrderCompleted}
        showAllProductsAvailability={showAllProductsAvailability}
        showMultipackProductsAvailability={showMultipackProductsAvailability}
        showOnInvoiceProductsAvailability={showOnInvoiceProductsAvailability}
        showGasProductsAvailability={showGasProductsAvailability}
        enableEditOrderedQuantities={!!deliveryDate && !isSaveOrderLoading}
        orderData={orderData}
        calculateOrder={calculateOrder}
        calculatedOrderResponse={calculatedOrderResponse}
        isPreviewOrderModalOpen={isPreviewOrderModalOpen}
        setIsPreviewOrderModalOpen={setIsPreviewOrderModalOpen}
        completeOrder={handleCompleteOrder}
        savedOrderResponse={savedOrderResponse}
        setOrderComment={setOrderComment}
        step={step}
        setStep={setStep}
      />

      <OrderInfoModal
        isOpen={isAvailabilityModalOpen}
        onClose={() => setIsAvailavilityModalOpen(false)}
      />

      <SuccessModal isOpen={isSuccessModalOpen} onClose={resetOrder} />
    </>
  );
};

export default CreateOrder;
